import { useTranslation } from 'react-i18next'
import { Button } from '@repo/studio-design-system-ui-button'
import { DotsGridIcon } from '@repo/studio-design-system-ui-icons/general/dots-grid'
import { Menu03Icon } from '@repo/studio-design-system-ui-icons/general/menu-03'
import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import { useAidaChat } from '../../context/aida-chat-context'
import { useAidaPopOut } from '../../hooks/use-aida-pop-out'
import { useFloatingDragHandle } from '../../hooks/use-floating-panel-position'
import { useAidaChatCommands } from '../../stores/chat-commands'
import { closeAidaOverlay, useAidaOverlayStore } from '../../stores/overlay-store'
import { useAidaPipStore } from '../../stores/picture-in-picture-store'
import { useResolvedAidaChatPresentation } from '../../utils/chat-presentation'
import { MascotHeaderAnchor, useHeaderMascotSlotOpen } from '../layout/mascot-presence'
import { MediaTabBar, useAidaMediaPane } from '../layout/media-tab'
import { AidaConversationMenu } from './aida-conversation-menu'
import { AidaViewModeSwitcher } from './aida-view-mode-switcher'
import { tw } from 'utils/classnames'

export function AidaPanelChrome({
  showHistory,
  historyClassName,
  onOpenHistory,
}: {
  showHistory: boolean
  historyClassName?: string
  onOpenHistory: () => void
}) {
  const { t } = useTranslation('components')
  const overlayOpen = useAidaOverlayStore(state => state.open)
  const presentation = useResolvedAidaChatPresentation()
  const dragHandle = useFloatingDragHandle()
  const { items } = useAidaMediaPane()
  const showTabs = items.length > 0
  const pipActive = useAidaPipStore(state => state.active)
  const showDrag = overlayOpen && presentation === 'float' && !pipActive
  const showWindowControls = overlayOpen && !pipActive
  const popOutOffer = usePopOutOffer()

  return (
    <header className="flex shrink-0 flex-col bg-theme_semantic_surface_primary-background-elevated">
      <div className="flex items-center gap-1 px-2 py-1.5">
        {showHistory ? (
          <Button
            iconOnly
            aria-label="Open chat history"
            variant="secondary"
            size="sm"
            className={tw('shrink-0', historyClassName)}
            onClick={onOpenHistory}
          >
            <Menu03Icon />
          </Button>
        ) : null}
        {showDrag ? <DragButton handle={dragHandle} /> : null}
        <AidaHeaderIdentity />
        {showWindowControls ? <AidaWindowControlButtons offer={popOutOffer} /> : null}
      </div>
      {showWindowControls && popOutOffer.showHint ? (
        <p className="ml-auto max-w-56 px-3 pb-1 text-right studio-typography-12 text-theme_semantic_surface_ui_foreground-color-subtle">
          {t('studio.components.aida.pop_out_hint')}
        </p>
      ) : null}
      {showTabs ? (
        <div className="flex h-7 shrink-0 items-center px-2">
          <MediaTabBar />
        </div>
      ) : null}
    </header>
  )
}

export function AidaAwayChrome({
  floatingDrag,
}: {
  floatingDrag: { handle: ReturnType<typeof useFloatingDragHandle> } | null
}) {
  const popOutOffer = usePopOutOffer()

  return (
    <header className="z-20 flex shrink-0 items-center gap-1 bg-theme_semantic_surface_primary-background-elevated p-2">
      {floatingDrag ? <DragButton handle={floatingDrag.handle} /> : null}
      <AidaWindowControlButtons offer={popOutOffer} />
    </header>
  )
}

function usePopOutOffer() {
  const { supported, active, popOut, bringBack } = useAidaPopOut()
  const generationActive = useAidaPipStore(state => state.generationActive)

  return {
    supported,
    active,
    popOut,
    bringBack,
    showHint: supported && generationActive && !active,
  }
}

function AidaWindowControlButtons({ offer }: { offer: ReturnType<typeof usePopOutOffer> }) {
  const { t } = useTranslation('components')
  const activeConversationId = useAidaChatCommands(state => state.activeConversationId)
  const { supported, active, popOut, bringBack } = offer

  return (
    <div className="ml-auto flex shrink-0 items-center gap-1">
      <AidaViewModeSwitcher
        pictureInPicture={supported ? { active, choose: active ? bringBack : popOut } : undefined}
      />
      <AidaConversationMenu conversationId={activeConversationId} includeNewChat reveal="always" />
      <Button
        iconOnly
        variant="ghost"
        size="sm"
        aria-label={t('studio.components.aida.close')}
        onClick={closeAidaOverlay}
      >
        <XCloseIcon />
      </Button>
    </div>
  )
}

function DragButton({ handle }: { handle: ReturnType<typeof useFloatingDragHandle> }) {
  const { t } = useTranslation('components')

  return (
    <Button
      {...handle}
      iconOnly
      size="sm"
      variant="ghost"
      className="shrink-0 touch-none cursor-grab"
      aria-label={t('studio.components.aida.move_panel')}
    >
      <DotsGridIcon />
    </Button>
  )
}

function headerStatusKey(isGenerating: boolean, busy: boolean) {
  if (isGenerating) {
    return 'studio.components.aida.status_generating' as const
  }
  if (busy) {
    return 'studio.components.aida.status_thinking' as const
  }
  return 'studio.components.aida.status_ready' as const
}

function AidaHeaderIdentity() {
  const slotOpen = useHeaderMascotSlotOpen()

  return (
    <div
      className={tw(
        'flex min-w-0 items-center transition-[gap] duration-200 ease-in-out motion-reduce:transition-none',
        slotOpen ? 'gap-1' : 'gap-0',
      )}
    >
      <MascotHeaderAnchor />
      <AidaHeaderStatus />
    </div>
  )
}

function AidaHeaderStatus() {
  const { t } = useTranslation('components')
  const { isGenerating, busy } = useAidaChat()
  const active = isGenerating || busy

  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate studio-typography-14 text-theme_semantic_surface_ui_foreground-color-strong">
        {t('studio.components.aida.title')}
      </span>
      <span
        className={tw(
          'truncate studio-typography-12',
          active
            ? 'text-theme_semantic_surface_ui_foreground-color-brand-regular'
            : 'text-theme_semantic_surface_ui_foreground-color-subtle',
        )}
      >
        {t(headerStatusKey(isGenerating, busy))}
      </span>
    </span>
  )
}
